import type { PendingItemKeyType } from '../../common/side-panel/pending-items/constants';
export const REMAINING_ITEMS_TYPES = Object.freeze({
  PENDING_ITEM: 'PENDING_ITEM',
  ERROR: 'ERROR'
});
export type RemainingItemsType = (typeof REMAINING_ITEMS_TYPES)[keyof typeof REMAINING_ITEMS_TYPES];

// Error type discriminator for RemainingItemsErrorT
export const RemainingItemsErrorType = ({
  SPELL_CHECK: 'spellCheck',
  MEDIA_DURATION: 'mediaDuration',
  TEXT_LENGTH: 'textLength',
  MISSING_REQUIRED: 'missingRequired',
  GAP_CONSTRAINT: 'gapConstraint',
  INSTANCE_COUNT: 'instanceCount',
  SOP: 'sop',
  MISSING_REQUIRED_DYNAMIC_ATTRIBUTE: 'missingRequiredDynamicAttribute',
  INVALID_BRACKETS: 'invalidBrackets',
  PRE_ANNOTATION_SLOT_COUNT_MISMATCH: 'preAnnotationSlotCountMismatch',
  RULE_ENGINE: 'rule-engine',
  GARBAGE_TRANSCRIPT: 'garbageTranscript',
  ABBREVIATION_SPACING: 'abbreviationSpacing',
  EMPHASIS_FORMATTING: 'emphasisFormatting',
  QUADRANT_IMBALANCE: 'quadrantImbalance',
  MINIMUM_ANNOTATIONS: 'minimumAnnotations'
} as const);
export type RemainingItemsErrorTypeT = (typeof RemainingItemsErrorType)[keyof typeof RemainingItemsErrorType];

// Error tag display types (configurable defaults)
export const ERROR_TAG_TYPE = ({
  SPELLING: 'Spelling',
  ML: 'ML',
  SOP: 'SOP',
  TAG: 'Tag',
  RULE_ENGINE: 'Rule Engine',
  QUADRANT_IMBALANCE: 'Quadrant Imbalance',
  MINIMUM_ANNOTATIONS: 'Minimum Annotations'
} as const);
export type ErrorTagTypeT = (typeof ERROR_TAG_TYPE)[keyof typeof ERROR_TAG_TYPE];

// Highlight types for transcript error highlighting
export const HighlightedItemType = ({
  SPELL_CHECK: 'spellCheck',
  GRAMMAR: 'grammar',
  FORMATTING: 'formatting'
} as const);
export type HighlightedItemTypeT = (typeof HighlightedItemType)[keyof typeof HighlightedItemType];
export const REMAINING_ITEM_OBJECT = ({
  SLOT: 'slot',
  ANNOTATION: 'annotation',
  TASK: 'task',
  TASK_QUESTIONNAIRE: 'task questionnaire',
  QUESTIONNAIRE_FIELD: 'questionnaire-field',
  AUDIO_NOT_LISTENED: 'audio-not-listened',
  RULE_ENGINE: 'rule-engine'
} as const);
export type RemainingItemsObjectType = (typeof REMAINING_ITEM_OBJECT)[keyof typeof REMAINING_ITEM_OBJECT];

// Object types that support "Go to" navigation functionality
export const NAVIGABLE_OBJECT_TYPES = new Set<RemainingItemsObjectType>([REMAINING_ITEM_OBJECT.SLOT, REMAINING_ITEM_OBJECT.ANNOTATION, REMAINING_ITEM_OBJECT.QUESTIONNAIRE_FIELD]);

// Task-level object types
export const TASK_LEVEL_OBJECT_TYPES = new Set<RemainingItemsObjectType>([REMAINING_ITEM_OBJECT.TASK, REMAINING_ITEM_OBJECT.TASK_QUESTIONNAIRE, REMAINING_ITEM_OBJECT.QUESTIONNAIRE_FIELD]);
export const REMAINING_ITEMS_MESSAGES = ({
  ATTRIBUTES_PENDING: 'Missing dynamic and static attributes',
  DYNAMIC_ATTRIBUTES_PENDING: 'Missing dynamic attributes',
  STATIC_ATTRIBUTES_PENDING: 'Missing static attributes',
  INCOMPLETE_QUESTIONNAIRE: 'Incomplete questionnaire'
} as const);
export type SidePanelOptionsT = {
  keepPanelOpen?: boolean;
  showGoToButton?: boolean;
  /** When false, prevents auto-navigation on expand even if showGoToButton is false. Default: true */
  autoNavigateOnExpand?: boolean;
};
export type SpellCheckMetadataT = {
  slotId: string;
  errorId: string;
  type: HighlightedItemTypeT;
};
export type MlFormatWarningKindT = 'garbageTranscript' | 'abbreviationSpacing' | 'emphasisFormatting';
export type MlFormatWarningMetadataT = {
  slotId: string;
  warningId: string;
  kind: MlFormatWarningKindT;
};
export type SopMetadataT = {
  slotId: string;
  errorId: string;
};
export type RemainingItemsMetadataT = {
  spellCheck?: SpellCheckMetadataT;
  mlFormatWarning?: MlFormatWarningMetadataT;
  sop?: SopMetadataT;
};

/**
 * Default button text for action handlers.
 */
export const DEFAULT_ACTION_BUTTON_TEXT = ({
  ACCEPT: 'Accept',
  IGNORE: 'Ignore'
} as const);

/**
 * Action handlers for remaining items (e.g., accept/ignore spell check errors).
 */
export type ActionHandlersT = {
  onAccept?: (metadata: RemainingItemsMetadataT) => void;
  onIgnore?: (metadata: RemainingItemsMetadataT) => void;
  /** Custom text for the accept button. Default: "Accept" */
  acceptText?: string;
  /** Custom text for the ignore button. Default: "Ignore" */
  ignoreText?: string;
};

/**
 * Map of action handlers keyed by metadata type (e.g., 'spellCheck', 'sop').
 * Allows different error types to have their own button text and behavior.
 */
export type ActionHandlersMapT = Partial<Record<keyof RemainingItemsMetadataT, ActionHandlersT>>;
export type RemainingItemsT = {
  id: string;
  label: string;
  objectType: RemainingItemsObjectType;
  type: RemainingItemsType;
  title?: string | React.ReactNode;
  subLabelOverride?: string;
  message: string | React.ReactNode;
  // Allows rendering custom component for user action
  customActions?: React.ReactNode;
  // Color for the annotation tag (optional)
  color?: {
    selected: string;
    unselected: string;
  };
  // Side panel behavior options for navigation and display
  sidePanelOptions?: SidePanelOptionsT;
};

/**
 * Derived type for error items with error type discriminator.
 * Use this type when creating error items to ensure proper error type is set.
 */
export type RemainingItemsErrorT = RemainingItemsT & {
  type: typeof REMAINING_ITEMS_TYPES.ERROR;
  // Error type discriminator (required for ERROR items, undefined for PENDING_ITEM)
  errorType: RemainingItemsErrorTypeT;
  // Metadata for error highlighting (e.g., spell check position data)
  metadata?: RemainingItemsMetadataT;
};
export type RemainingItemsPendingT = RemainingItemsT & {
  type: typeof REMAINING_ITEMS_TYPES.PENDING_ITEM;
  // Error type discriminator (required for ERROR items, undefined for PENDING_ITEM)
  pendingItemType: PendingItemKeyType;
};
export type RemainingItemsDetailsT = (RemainingItemsT & RemainingItemsErrorT) | (RemainingItemsT & RemainingItemsPendingT);

/**
 * Grouped item structure for displaying items by speaker-slot combination or task.
 */
export type GroupedRemainingItemsT = {
  // Unique key for the group (e.g., "Speaker 1 - Slot 1" or "task")
  groupKey: string;
  // Display label for the group header
  groupLabel: string;
  // Number of items in the group
  itemCount: number;
  // Items in this group
  items: RemainingItemsDetailsT[];
  // Optional color for the group tag
  color?: {
    selected: string;
    unselected: string;
  };
  // Primary object type for navigation behavior
  objectType: RemainingItemsObjectType;
};

/**
 * Configuration for grouping and tag display.
 */
export type GroupingConfig = {
  // Custom error type to tag mapping (overrides defaults)
  errorTagMap?: Partial<Record<RemainingItemsErrorTypeT, string>>;
  // Whether to show tags for task-level errors (default: false)
  showTaskLevelTags?: boolean;
  // Custom tag mapping for task-level errors
  taskLevelTagMap?: Partial<Record<RemainingItemsErrorTypeT, string>>;
  // Custom label for the task group (default: "Task")
  taskGroupLabel?: string;
};